/**
 * แปลและรับรองเอกสาร — Wave 3 (destination-country routing).
 *
 * แกนหลักของเวฟนี้คือ "ระบบคำแปลที่ประเทศปลายทางยอมรับ" ซึ่งต่างกันเป็นรายประเทศ
 * (sworn / assermenté / jurado / beeidigt / NAATI / certified + Certificate of Accuracy)
 * และทิศทางเอกสารขาเข้าประเทศไทย
 *
 * ข้อเท็จจริงที่ยึด:
 * - 4 ชั้นแยกกัน: แปล → คำรับรองของผู้แปล → รับรองลายมือชื่อ (ทนายผู้ทำคำรับรองฯ)
 *   → นิติกรณ์/Legalisation (กรมการกงสุล → Apostille หรือสถานทูตปลายทาง)
 * - ประเทศไทยยังไม่ออกตรา Apostille จนกว่าอนุสัญญากรุงเฮก ค.ศ. 1961 จะมีผลกับไทย
 *   วันที่ 28 กุมภาพันธ์ 2027 (ดู src/data/legal-facts.ts) ระหว่างนี้ใช้สายกรมการกงสุล → สถานทูต
 * - ค่าธรรมเนียมนิติกรณ์กรมการกงสุลเป็นค่าธรรมเนียมราชการ (ปกติ 200 บาท/ตราประทับ,
 *   ด่วน 400 บาท/ตราประทับ ตาม consular.mfa.go.th) ไม่ใช่ค่าบริการแปล
 * - ไม่ระบุค่าบริการของสำนักงาน และไม่รับประกันผลการพิจารณาของหน่วยงานปลายทาง
 *
 * ข้อมูล ณ ส.ค. 2026 — โปรดตรวจสอบข้อกำหนดล่าสุดกับหน่วยงานผู้รับก่อนยื่นทุกครั้ง
 */
import type { MltQA } from "./questions-500";

const PHONE = "094-895-8999";
const CTA = `Thai Notary Law · โทร ${PHONE} · LINE @Thainotary — แปล รับรองคำแปล และเดินเรื่องนิติกรณ์ครบวงจร`;
const DISCLAIMER =
  "ข้อมูล ณ ส.ค. 2026 · ระยะเวลาเป็นวันทำการและขึ้นกับคิวของหน่วยงาน · โปรดตรวจสอบข้อกำหนดล่าสุดกับหน่วยงานผู้รับปลายทางก่อนยื่น";

type Dest = {
  slug: string;
  th: string;
  en: string;
  /** ระบบคำแปลที่ปลายทางยอมรับ */
  system: string;
  /** คำเรียกท้องถิ่นของคำแปลรับรอง */
  localName: string;
  /** ข้อควรระวังเฉพาะประเทศ */
  caveat: string;
  /** ภาคีอนุสัญญาเฮก Apostille */
  hague: boolean;
  /** หน่วยงานผู้รับที่พบบ่อย */
  receiver: string;
};

const DESTS: Dest[] = [
  {
    slug: "australia", th: "ออสเตรเลีย", en: "Australia",
    system: "คำแปลโดยนักแปลที่ได้รับการรับรองจาก NAATI (Certified Translator) พร้อมตราประทับที่แสดง CPN ทิศทางภาษา และวันหมดอายุ",
    localName: "NAATI certified translation",
    caveat: "NAATI รับรอง 'บุคคล' ไม่ใช่บริษัท และรับรองเป็นรายทิศทางภาษา (Thai→English ไม่เท่ากับ English→Thai) · กฎของ Department of Home Affairs แยกชัดระหว่างคำแปลที่ทำ 'ในออสเตรเลีย' (ต้องเป็นนักแปล NAATI) กับคำแปลที่ทำ 'นอกออสเตรเลีย' (ผู้แปลต้องระบุชื่อเต็ม ที่อยู่ ข้อมูลติดต่อ คุณสมบัติและประสบการณ์) ดู immi.homeaffairs.gov.au",
    hague: true, receiver: "Department of Home Affairs, มหาวิทยาลัย, หน่วยงาน skills assessment",
  },
  {
    slug: "new-zealand", th: "นิวซีแลนด์", en: "New Zealand",
    system: "คำแปลจากนักแปลที่ได้รับการรับรอง (มัก NAATI หรือ NZSTI) พร้อม Certificate of Accuracy",
    localName: "certified translation",
    caveat: "Immigration New Zealand กำหนดให้คำแปลต้องไม่ทำโดยผู้สมัครเองหรือสมาชิกในครอบครัว และต้องระบุผู้แปลอย่างครบถ้วน",
    hague: true, receiver: "Immigration New Zealand, NZQA",
  },
  {
    slug: "usa", th: "สหรัฐอเมริกา", en: "United States",
    system: "Certified translation แนบ Certificate of Accuracy ระบุชื่อ ที่อยู่ คุณสมบัติผู้แปล และคำรับรองว่าคำแปลถูกต้องครบถ้วน",
    localName: "certified translation",
    caveat: "USCIS ไม่ได้กำหนดให้ผู้แปลต้องขึ้นทะเบียนกับหน่วยงานใด แต่บังคับว่าต้องมีคำรับรองความถูกต้อง · Apostille ในสหรัฐฯ ออกโดยหน่วยงานของแต่ละมลรัฐหรือ US Department of State แล้วแต่ผู้ออกเอกสาร",
    hague: true, receiver: "USCIS, สถานทูต/สถานกงสุลสหรัฐฯ, มหาวิทยาลัย",
  },
  {
    slug: "canada", th: "แคนาดา", en: "Canada",
    system: "คำแปลจาก certified translator (สมาชิก ATIO / OTTIAQ / STIBC) หรือคำแปลพร้อม affidavit ต่อหน้า commissioner of oaths",
    localName: "traduction certifiée / certified translation",
    caveat: "IRCC ไม่รับคำแปลที่ทำโดยผู้สมัครเอง สมาชิกครอบครัว หรือที่ปรึกษาที่เป็นตัวแทน · หากผู้แปลไม่ใช่ certified translator ต้องแนบ affidavit",
    hague: true, receiver: "IRCC, WES/ICES (ECA), มหาวิทยาลัย",
  },
  {
    slug: "uk", th: "สหราชอาณาจักร", en: "United Kingdom",
    system: "Certified translation พร้อมคำรับรองของผู้แปล (นิยมสมาชิก ITI หรือ CIOL) ระบุวันที่ ชื่อ และข้อมูลติดต่อ",
    localName: "certified translation",
    caveat: "UK Visas and Immigration กำหนดให้คำแปลต้องยืนยันว่าเป็นคำแปลที่ถูกต้องของต้นฉบับ พร้อมวันที่และรายละเอียดผู้แปล — ไม่ต้อง notarise ทุกกรณี",
    hague: true, receiver: "UKVI, Home Office, มหาวิทยาลัย",
  },
  {
    slug: "germany", th: "เยอรมนี", en: "Germany",
    system: "คำแปลโดย vereidigter/beeidigter Übersetzer (นักแปลที่ศาลเยอรมันแต่งตั้ง) หรือคำแปลจากไทยที่ผ่านนิติกรณ์กรมการกงสุลและสถานทูตเยอรมนีตามที่หน่วยงานปลายทางกำหนด",
    localName: "beglaubigte Übersetzung",
    caveat: "หน่วยงานเยอรมันจำนวนมากรับเฉพาะคำแปลของนักแปลที่ขึ้นทะเบียนกับศาลเยอรมัน เราไม่ใช่ sworn translator ของเยอรมนี จึงต้องตรวจกับผู้รับก่อนว่ารับคำแปลจากไทยหรือไม่ · สถานทูตเยอรมนีมีระบบตรวจสอบเอกสารไทย (Legalisation/Urkundenüberprüfung) ที่ใช้เวลานานกว่าปกติ",
    hague: true, receiver: "Standesamt, Ausländerbehörde, สถานทูตเยอรมนี, มหาวิทยาลัย",
  },
  {
    slug: "france", th: "ฝรั่งเศส", en: "France",
    system: "คำแปลโดย traducteur assermenté ที่ขึ้นทะเบียนกับ Cour d'appel หรือคำแปลจากไทยที่ผ่านกรมการกงสุลและสถานทูตฝรั่งเศส แล้วแต่ข้อกำหนดของผู้รับ",
    localName: "traduction assermentée",
    caveat: "เราไม่ใช่ traducteur assermenté ของฝรั่งเศส หากผู้รับกำหนดชัดว่าต้องเป็น assermenté ต้องแปลที่ฝรั่งเศส · เอกสารทะเบียนราษฎรฝรั่งเศสมักกำหนดอายุไม่เกิน 3-6 เดือน",
    hague: true, receiver: "Mairie, Préfecture, สถานกงสุลฝรั่งเศส, มหาวิทยาลัย",
  },
  {
    slug: "spain", th: "สเปน", en: "Spain",
    system: "คำแปลโดย traductor jurado ที่ขึ้นทะเบียนกับกระทรวงการต่างประเทศสเปน (MAEC)",
    localName: "traducción jurada",
    caveat: "สเปนเข้มงวดเรื่อง traductor jurado มาก คำแปลจากไทยมักใช้ได้เฉพาะเมื่อผ่านสถานทูตสเปนและผู้รับยอมรับเป็นรายกรณี",
    hague: true, receiver: "Registro Civil, Extranjería, มหาวิทยาลัย",
  },
  {
    slug: "italy", th: "อิตาลี", en: "Italy",
    system: "คำแปลที่ผ่าน asseverazione (สาบานคำแปลต่อศาล) ในอิตาลี หรือคำแปลที่รับรองโดยสถานทูตอิตาลีในไทย",
    localName: "traduzione giurata",
    caveat: "สถานทูตอิตาลีในไทยมีรายชื่อผู้แปลที่ยอมรับและขั้นตอน dichiarazione di valore สำหรับเอกสารการศึกษา ควรตรวจสอบก่อนเริ่มงาน",
    hague: true, receiver: "Comune, Questura, สถานทูตอิตาลี, มหาวิทยาลัย",
  },
  {
    slug: "netherlands", th: "เนเธอร์แลนด์", en: "Netherlands",
    system: "คำแปลโดย beëdigd vertaler ที่ขึ้นทะเบียน Rbtv หรือคำแปลจากไทยที่ผ่านนิติกรณ์และสถานทูตตามข้อกำหนดของผู้รับ",
    localName: "beëdigde vertaling",
    caveat: "เทศบาล (gemeente) ของเนเธอร์แลนด์มักกำหนดให้เอกสารทะเบียนราษฎรผ่าน legalisatie ครบชั้นก่อนบันทึกในระบบ BRP",
    hague: true, receiver: "Gemeente, IND, DUO",
  },
  {
    slug: "belgium", th: "เบลเยียม", en: "Belgium",
    system: "คำแปลโดย beëdigd vertaler / traducteur juré ที่ขึ้นทะเบียนในทะเบียนกลางของเบลเยียม",
    localName: "traduction jurée / beëdigde vertaling",
    caveat: "เบลเยียมมี 3 ภาษาราชการ (ดัตช์ ฝรั่งเศส เยอรมัน) ต้องยืนยันภาษาปลายทางตามภูมิภาคของหน่วยงานผู้รับก่อนแปล",
    hague: true, receiver: "Commune/Gemeente, Dienst Vreemdelingenzaken",
  },
  {
    slug: "switzerland", th: "สวิตเซอร์แลนด์", en: "Switzerland",
    system: "คำแปลรับรองพร้อมรับรองลายมือชื่อผู้แปลโดย notary และ legalisation ตามข้อกำหนดของแคนทอน",
    localName: "beglaubigte Übersetzung / traduction certifiée",
    caveat: "ข้อกำหนดต่างกันรายแคนทอน (Zürich, Genève, Bern ไม่เหมือนกัน) ต้องถามหน่วยงานผู้รับเป็นรายแคนทอน",
    hague: true, receiver: "Zivilstandsamt, Migrationsamt ของแต่ละแคนทอน",
  },
  {
    slug: "sweden", th: "สวีเดน", en: "Sweden",
    system: "คำแปลโดย auktoriserad translator ที่ได้รับอนุญาตจาก Kammarkollegiet หรือคำแปลอังกฤษที่ผ่านนิติกรณ์",
    localName: "auktoriserad översättning",
    caveat: "Skatteverket และ Migrationsverket มักรับคำแปลภาษาอังกฤษได้ แต่ต้องผ่าน legalisation ครบชั้นจากไทย",
    hague: true, receiver: "Migrationsverket, Skatteverket",
  },
  {
    slug: "norway", th: "นอร์เวย์", en: "Norway",
    system: "คำแปลโดย statsautorisert translatør หรือคำแปลอังกฤษที่ผ่านการรับรองครบชั้น",
    localName: "autorisert oversettelse",
    caveat: "UDI กำหนดรูปแบบเอกสารและคำแปลค่อนข้างเข้ม ควรตรวจ checklist ของ udi.no ก่อนยื่น",
    hague: true, receiver: "UDI, Folkeregisteret",
  },
  {
    slug: "denmark", th: "เดนมาร์ก", en: "Denmark",
    system: "คำแปลรับรองภาษาอังกฤษหรือเดนมาร์ก พร้อม legalisation ตามข้อกำหนดของหน่วยงานผู้รับ",
    localName: "certified translation",
    caveat: "เดนมาร์กยกเลิกระบบ statsautoriseret translatør แบบเดิมแล้ว หน่วยงานจึงพิจารณาคุณสมบัติผู้แปลเป็นรายกรณี",
    hague: true, receiver: "SIRI, Familieretshuset",
  },
  {
    slug: "poland", th: "โปแลนด์", en: "Poland",
    system: "คำแปลโดย tłumacz przysięgły ที่ขึ้นทะเบียนกับกระทรวงยุติธรรมโปแลนด์",
    localName: "tłumaczenie przysięgłe",
    caveat: "หน่วยงานโปแลนด์แทบทั้งหมดรับเฉพาะคำแปลของ tłumacz przysięgły เท่านั้น คำแปลจากไทยมักใช้เป็นเอกสารประกอบได้เท่านั้น",
    hague: true, receiver: "Urząd Stanu Cywilnego, Urząd Wojewódzki",
  },
  {
    slug: "czech", th: "เช็กเกีย", en: "Czechia",
    system: "คำแปลโดย soudní tlumočník (นักแปลที่ศาลแต่งตั้ง)",
    localName: "soudní překlad",
    caveat: "เอกสารไทยต้องผ่านกรมการกงสุลและสถานทูตเช็กก่อน แล้วจึงแปลโดย soudní tlumočník ในเช็กเกีย",
    hague: true, receiver: "Matriční úřad, Ministerstvo vnitra",
  },
  {
    slug: "japan", th: "ญี่ปุ่น", en: "Japan",
    system: "คำแปลพร้อมคำรับรองของผู้แปล และในบางกรณีต้องรับรองที่ 公証役場 (notary office) ตามที่ผู้รับกำหนด",
    localName: "翻訳証明",
    caveat: "หน่วยงานญี่ปุ่นเน้นความตรงของการถอดชื่อเป็นคาตากานะและตัวสะกดโรมันจิให้ตรงพาสปอร์ต · ญี่ปุ่นเป็นภาคีอนุสัญญาเฮก",
    hague: true, receiver: "入国管理局 (ตม.ญี่ปุ่น), 市役所, มหาวิทยาลัย",
  },
  {
    slug: "korea", th: "เกาหลีใต้", en: "South Korea",
    system: "คำแปลรับรอง และมักต้องผ่าน 번역공증 (notarisation ของคำแปล) ตามที่หน่วยงานผู้รับกำหนด",
    localName: "번역공증",
    caveat: "เอกสารไทยต้องผ่านนิติกรณ์กรมการกงสุลและสถานทูตเกาหลีในไทยก่อน จึงนำไปใช้กับหน่วยงานเกาหลีได้",
    hague: true, receiver: "출입국·외국인청 (ตม.เกาหลี), 구청, มหาวิทยาลัย",
  },
  {
    slug: "china", th: "จีน", en: "China",
    system: "คำแปลภาษาจีนตัวย่อ พร้อมการรับรองตามช่องทางที่หน่วยงานจีนกำหนด (公证处 หรือสถานทูตจีน)",
    localName: "翻译公证",
    caveat: "ต้องแยกจีนตัวย่อ (จีนแผ่นดินใหญ่) กับตัวเต็ม (ฮ่องกง ไต้หวัน) ให้ถูก · แม้จีนเป็นภาคีอนุสัญญาเฮกแล้ว บางหน่วยงานยังขอสายรับรองแบบเดิม จึงต้องตรวจกับผู้รับปลายทางก่อน",
    hague: true, receiver: "公安局出入境, 民政局, มหาวิทยาลัย",
  },
  {
    slug: "taiwan", th: "ไต้หวัน", en: "Taiwan",
    system: "คำแปลรับรอง แล้วรับรองที่สำนักงานเศรษฐกิจและวัฒนธรรมไทเป (TECO) ประจำประเทศไทย",
    localName: "認證翻譯",
    caveat: "ไต้หวันไม่ใช่ภาคีอนุสัญญาเฮก จึงใช้สายรับรองเดิมเสมอ: กรมการกงสุล → TECO",
    hague: false, receiver: "移民署 (ตม.ไต้หวัน), 戶政事務所",
  },
  {
    slug: "vietnam", th: "เวียดนาม", en: "Vietnam",
    system: "คำแปลภาษาเวียดนามพร้อม công chứng (notarisation) ในเวียดนาม หรือคำแปลอังกฤษที่ผ่านสถานทูตเวียดนามในไทย",
    localName: "dịch công chứng",
    caveat: "เวียดนามเป็นภาคีอนุสัญญาเฮกแล้ว แต่หน่วยงานท้องถิ่นหลายแห่งยังคุ้นกับสายรับรองผ่านสถานทูต ควรยืนยันกับผู้รับก่อน",
    hague: true, receiver: "Sở Tư pháp, Cục Quản lý xuất nhập cảnh",
  },
  {
    slug: "uae", th: "สหรัฐอาหรับเอมิเรตส์", en: "United Arab Emirates",
    system: "คำแปลภาษาอาหรับโดย legal translator ที่ขึ้นทะเบียนกับกระทรวงยุติธรรม UAE พร้อม attestation ครบชั้น",
    localName: "legal translation",
    caveat: "สายรับรองยาว: กรมการกงสุลไทย → สถานทูต UAE ในไทย → MOFAIC ที่ UAE → แปลโดย legal translator ที่ขึ้นทะเบียน · แม้ UAE เข้าเป็นภาคีอนุสัญญาเฮกแล้ว หลายหน่วยงานยังกำหนด attestation ตามเดิม",
    hague: true, receiver: "MOHRE, GDRFA, มหาวิทยาลัยและนายจ้างใน UAE",
  },
  {
    slug: "saudi-arabia", th: "ซาอุดีอาระเบีย", en: "Saudi Arabia",
    system: "คำแปลภาษาอาหรับ พร้อม attestation จากสถานทูตซาอุดีอาระเบียในไทยและหน่วยงานที่ซาอุฯ กำหนด",
    localName: "legal translation",
    caveat: "เอกสารการศึกษาและเอกสารการแพทย์มักต้องผ่านการตรวจสอบเพิ่มเติมจากหน่วยงานวิชาชีพของซาอุฯ ก่อนรับรอง",
    hague: false, receiver: "กระทรวงแรงงานซาอุฯ, สถานทูตซาอุดีอาระเบีย",
  },
  {
    slug: "india", th: "อินเดีย", en: "India",
    system: "คำแปลภาษาอังกฤษพร้อมคำรับรองผู้แปล และการรับรองตามสายของหน่วยงานผู้รับ",
    localName: "certified translation",
    caveat: "เอกสารอินเดียที่จะใช้ในไทยออก Apostille โดย MEA ของอินเดีย ส่วนเอกสารไทยที่จะใช้ในอินเดียยังต้องผ่านกรมการกงสุลและสถานทูตอินเดียจนกว่าอนุสัญญาเฮกจะมีผลกับไทย",
    hague: true, receiver: "FRRO, สถานทูตอินเดีย, มหาวิทยาลัย",
  },
  {
    slug: "singapore", th: "สิงคโปร์", en: "Singapore",
    system: "คำแปลภาษาอังกฤษพร้อม Certificate of Accuracy และรับรองลายมือชื่อผู้แปลเมื่อผู้รับกำหนด",
    localName: "certified translation",
    caveat: "ICA และ MOM มักรับคำแปลอังกฤษที่มีคำรับรองผู้แปล แต่บางกรณีกำหนดให้รับรองที่สถานทูตสิงคโปร์หรือ notary",
    hague: true, receiver: "ICA, MOM, มหาวิทยาลัย",
  },
];

const dq = (d: Dest, suffix: string) => `mlt-dest-${d.slug}-${suffix}`;

/** ขาออก: เอกสารไทย → ประเทศปลายทาง */
const OUTBOUND: MltQA[] = DESTS.map((d) => ({
  slug: dq(d, "outbound"),
  q: `ส่งเอกสารไทยไปใช้ที่${d.th} ต้องใช้คำแปลแบบไหนและผ่านกี่ขั้น?`,
  aShort: `${d.en} ยอมรับ${d.system} · เส้นทางมาตรฐานจากไทยคือ คัดเอกสารฉบับใหม่จากหน่วยงานผู้ออก → แปล → รับรองคำแปล → นิติกรณ์กรมการกงสุล → ${d.hague ? "สถานทูต" + d.th + "ในไทย (จนกว่าอนุสัญญาเฮกจะมีผลกับไทยวันที่ 28 กุมภาพันธ์ 2027 จึงเปลี่ยนเป็น Apostille)" : "สถานทูต" + d.th + "ในไทย (" + d.th + "ไม่ใช่ภาคีอนุสัญญาเฮก จึงใช้สายสถานทูตเสมอ)"}`,
  aLong: `แยก 4 ชั้นให้ชัดก่อนเริ่มงาน: (1) การแปลเนื้อหา (2) คำรับรองความถูกต้องของผู้แปล (3) การรับรองลายมือชื่อโดยทนายผู้ทำคำรับรองลายมือชื่อและเอกสาร ขึ้นทะเบียนสภาทนายความฯ เมื่อเป็นเอกสารเอกชน (4) นิติกรณ์/legalisation เพื่อให้ใช้ข้ามประเทศ — ชั้น 1-3 ไม่แทนชั้น 4 · ระบบคำแปลที่${d.th}ยอมรับ: ${d.system} · ข้อควรระวังเฉพาะประเทศ: ${d.caveat} · หน่วยงานผู้รับที่พบบ่อย: ${d.receiver} · ค่าธรรมเนียมนิติกรณ์ของกรมการกงสุลเป็นค่าธรรมเนียมราชการ (ปกติ 200 บาท/ตราประทับ, ด่วน 400 บาท/ตราประทับ ตาม consular.mfa.go.th) แยกจากค่าบริการแปล · ${DISCLAIMER} · ${CTA}`,
  synonyms: [
    `แปลเอกสารไป${d.th}`,
    `certified translation for ${d.en.toLowerCase()}`,
    `${d.localName} thailand`,
    `รับรองเอกสารไปใช้${d.th}`,
  ],
  cluster: "certification" as const,
}));

/** ขาเข้า: เอกสารจากประเทศต้นทาง → ใช้ในไทย */
const INBOUND: MltQA[] = DESTS.map((d) => ({
  slug: dq(d, "inbound"),
  q: `เอกสารที่ออกจาก${d.th} จะนำมาใช้กับราชการไทยต้องทำอย่างไร?`,
  aShort: `รับรองที่ต้นทางก่อน (${d.hague ? "Apostille จากหน่วยงานผู้มีอำนาจของ" + d.th : "notary + หน่วยงานผู้มีอำนาจของ" + d.th + " แล้วรับรองที่สถานทูตไทย"}) → แปลเป็นไทย → กรมการกงสุลรับรองคำแปล → ยื่นหน่วยงานไทย เช่น สำนักงานเขต/อำเภอ ตม. ศาล หรือ DBD`,
  aLong: `ลำดับสำหรับเอกสารขาเข้า: (1) ${d.hague ? "ขอ Apostille หรือการรับรองจากหน่วยงานผู้มีอำนาจของ" + d.th + " ตามที่หน่วยงานไทยผู้รับกำหนด" : d.th + "ไม่ใช่ภาคีอนุสัญญาเฮก จึงต้องผ่าน notary และหน่วยงานผู้มีอำนาจของประเทศต้นทาง แล้วรับรองที่สถานเอกอัครราชทูตไทย"} (2) แปลเป็นภาษาไทยโดยผู้แปลที่ออกหนังสือรับรองความถูกต้องได้ (3) ยื่นกองสัญชาติและนิติกรณ์ กรมการกงสุล เพื่อรับรองคำแปล (4) ยื่นหน่วยงานไทยผู้รับ · ข้อควรระวังของเอกสารจาก${d.th}: ${d.caveat} · จุดที่ถูกตีกลับบ่อยคือชื่อสะกดไม่ตรงพาสปอร์ต ใช้สำเนาถ่ายแทนฉบับคัดรับรอง และเอกสารหมดอายุระหว่างรอคิว · ${DISCLAIMER} · ${CTA}`,
  synonyms: [
    `เอกสาร${d.th}ใช้ในไทย`,
    `${d.en.toLowerCase()} documents use in thailand`,
    `แปลเอกสาร${d.th}เป็นไทย`,
    `legalisation ${d.en.toLowerCase()} to thailand`,
  ],
  cluster: "authority" as const,
}));

/** ปัญหาที่พบบ่อย + เปรียบเทียบชั้นการรับรอง */
const TROUBLE: MltQA[] = [
  {
    slug: "mlt-4-layers-difference",
    q: "แปล · รับรองคำแปล · Notary · นิติกรณ์ ต่างกันอย่างไร?",
    aShort:
      "เป็น 4 ชั้นคนละหน้าที่: (1) แปล = ถ่ายทอดเนื้อหา (2) รับรองคำแปล = ผู้แปลลงนามยืนยันความถูกต้อง (3) Notary = ทนายผู้ทำคำรับรองฯ รับรองลายมือชื่อผู้ลงนาม ไม่ได้รับรองเนื้อหาคำแปล (4) นิติกรณ์/Legalisation = ทำให้เอกสารใช้ข้ามประเทศได้",
    aLong:
      "ความเข้าใจผิดที่ทำให้ถูกตีกลับบ่อยที่สุดคือคิดว่าชั้น 1-3 แทนชั้น 4 ได้ · ในไทยผู้ทำหน้าที่ชั้น 3 คือทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร ซึ่งขึ้นทะเบียนกับสภาทนายความในพระบรมราชูปถัมภ์ ไม่ใช่ Notary Public ตามระบบคอมมอนลอว์ · ชั้น 4 ในไทยดำเนินการโดยกองสัญชาติและนิติกรณ์ กรมการกงสุล กระทรวงการต่างประเทศ แล้วต่อด้วยสถานทูตปลายทางเมื่อประเทศนั้นกำหนด · " +
      DISCLAIMER + " · " + CTA,
    synonyms: ["notary กับ รับรองคำแปล ต่างกัน", "legalisation vs notarisation", "นิติกรณ์คืออะไร"],
    cluster: "compare",
  },
  {
    slug: "mlt-rejection-top-reasons",
    q: "คำแปลถูกตีกลับเพราะอะไรบ่อยที่สุด และป้องกันอย่างไร?",
    aShort:
      "อันดับต้น ๆ คือ ชื่อสะกดไม่ตรงพาสปอร์ต · ใช้สำเนาถ่ายแทนฉบับคัดรับรองใหม่ · ใช้ระบบคำแปลผิดประเทศ · รับรองต้นฉบับแล้วลืมรับรองคำแปล · เอกสารหมดอายุระหว่างรอคิว · แกะแม็กซ์จนตราประทับขาด",
    aLong:
      "วิธีป้องกันที่ได้ผลจริง: ส่งสำเนาหน้าพาสปอร์ตมาพร้อมต้นฉบับเพื่อล็อกการสะกดชื่อทุกภาษา · คัดเอกสารทะเบียนราษฎรฉบับใหม่ก่อนเริ่มงานเมื่อปลายทางกำหนดอายุ 3-6 เดือน · ยืนยันกับหน่วยงานผู้รับก่อนว่ารับคำแปลรูปแบบใด (certified / sworn / NAATI / notarised) · แปลทั้งตราประทับ ลายเซ็น และข้อความด้านหลังเอกสาร · ไม่แยกชุดเอกสารที่เย็บและประทับตราคร่อมแล้ว · เราตรวจ checklist ให้ก่อนยื่นทุกครั้งเพื่อลดโอกาสถูกตีกลับ แต่การพิจารณาเป็นดุลพินิจของหน่วยงานผู้รับ · " +
      DISCLAIMER + " · " + CTA,
    synonyms: ["คำแปลถูกปฏิเสธ", "translation rejected embassy", "เอกสารตีกลับ"],
    cluster: "troubleshoot",
  },
  {
    slug: "mlt-apostille-thailand-status",
    q: "ประเทศไทยออก Apostille ได้แล้วหรือยัง?",
    aShort:
      "ยังไม่ได้ — อนุสัญญากรุงเฮก ค.ศ. 1961 จะมีผลบังคับใช้กับประเทศไทยวันที่ 28 กุมภาพันธ์ 2027 ระหว่างนี้เอกสารไทยยังใช้สายรับรองเดิม คือ นิติกรณ์กรมการกงสุล แล้วต่อสถานทูตประเทศปลายทางเมื่อประเทศนั้นกำหนด",
    aLong:
      "สิ่งที่ Apostille ทำคือรับรองลายมือชื่อและตราประทับของผู้ลงนามในเอกสาร ไม่ได้รับรองเนื้อหา และไม่แทนขั้นตอนการแปลหรือการรับรองคำแปล · เมื่ออนุสัญญามีผลกับไทยแล้ว Apostille จะแทนเฉพาะขั้น 'รับรองโดยสถานทูตปลายทาง' สำหรับประเทศที่เป็นภาคีเท่านั้น ประเทศนอกภาคี เช่น ไต้หวัน ยังต้องใช้สายเดิม · เอกสารขาเข้าจากประเทศภาคีที่มี Apostille แล้ว ยังต้องแปลเป็นไทยและให้กรมการกงสุลรับรองคำแปลก่อนยื่นหน่วยงานไทย · แหล่งอ้างอิง: hcch.net และ consular.mfa.go.th · " +
      DISCLAIMER + " · " + CTA,
    synonyms: ["apostille ไทย เมื่อไหร่", "thailand apostille 2027", "hague convention thailand"],
    cluster: "certification",
  },
  {
    slug: "mlt-name-spelling-passport",
    q: "สะกดชื่อในคำแปลอย่างไรให้ไม่ถูกตีกลับ?",
    aShort:
      "ยึดการสะกดตามหน้าพาสปอร์ตเป็นหลักเสมอ และใช้การสะกดเดียวกันในทุกเอกสารของชุดเดียวกัน หากเคยยื่นวีซ่าหรือมีเอกสารเก่าที่สะกดต่างออกไป ให้แจ้งล่วงหน้าเพื่อทำหมายเหตุผู้แปล",
    aLong:
      "กรณีที่พบบ่อย: ชื่อไทยถอดเป็นโรมันได้หลายแบบ (เช่น สุวรรณ = Suwan / Suvarn) · เอกสารการศึกษาสะกดตามระบบของสถานศึกษาซึ่งอาจต่างจากพาสปอร์ต · ชื่อหลังสมรสยังไม่ปรับในทะเบียนบ้าน · แนวทางแก้: แนบสำเนาพาสปอร์ตทุกครั้ง ระบุการสะกดที่ต้องการเป็นลายลักษณ์อักษร และให้ผู้แปลใส่หมายเหตุอธิบายความต่างของการสะกดพร้อมอ้างอิงเลขที่เอกสาร เพื่อให้เจ้าหน้าที่ปลายทางเชื่อมโยงตัวบุคคลได้ · " +
      DISCLAIMER + " · " + CTA,
    synonyms: ["สะกดชื่อในคำแปล", "name spelling passport translation", "ชื่อไม่ตรงพาสปอร์ต"],
    cluster: "troubleshoot",
  },
  {
    slug: "mlt-document-validity-period",
    q: "เอกสารที่จะแปลและรับรองมีอายุใช้งานนานแค่ไหน?",
    aShort:
      "หน่วยงานปลายทางหลายแห่งกำหนดให้เอกสารทะเบียนราษฎรและหนังสือรับรองสถานภาพต้องคัดใหม่ไม่เกิน 3-6 เดือน ส่วนใบรับรองความประพฤติและหนังสือรับรองนิติบุคคลมักกำหนดสั้นกว่านั้น",
    aLong:
      "อายุเอกสารเป็นกฎของหน่วยงานผู้รับ ไม่ใช่ของผู้แปล จึงต้องตรวจกับผู้รับปลายทางเป็นรายกรณี · ข้อควรวางแผน: นับเวลาถอยหลังจากวันนัดยื่น แล้วเผื่อคิวนิติกรณ์และคิวสถานทูต เพื่อไม่ให้เอกสารหมดอายุระหว่างรอ · หนังสือรับรองนิติบุคคลจากกรมพัฒนาธุรกิจการค้ามักถูกกำหนดไม่เกิน 6 เดือน · ใบรับรองความประพฤติจากสำนักงานตำรวจแห่งชาติควรทำใกล้วันยื่นที่สุด · " +
      DISCLAIMER + " · " + CTA,
    synonyms: ["เอกสารอายุกี่เดือน", "document validity translation", "หนังสือรับรองโสด อายุ"],
    cluster: "documents",
  },
  {
    slug: "mlt-digital-vs-paper",
    q: "คำแปลแบบไฟล์ดิจิทัลกับฉบับกระดาษ ใช้แทนกันได้ไหม?",
    aShort:
      "ขึ้นกับหน่วยงานผู้รับ — พอร์ทัลยื่นออนไลน์หลายแห่งรับ PDF ที่ลงลายเซ็นดิจิทัลตรวจสอบได้ ส่วนงานนิติกรณ์กรมการกงสุลและสถานทูตยังต้องใช้ฉบับกระดาษพร้อมต้นฉบับ",
    aLong:
      "ข้อควรระวังของไฟล์ดิจิทัล: การพิมพ์ออกแล้วสแกนซ้ำจะทำให้ลายเซ็นดิจิทัลตรวจสอบไม่ได้ และหน่วยงานอาจถือว่าเป็นสำเนาที่ไม่ได้รับรอง · แนวปฏิบัติที่ปลอดภัย: ถามผู้รับก่อนว่าต้องการรูปแบบใด แล้วสั่งทำทั้งสองรูปแบบเมื่อไม่แน่ใจ เพราะการทำย้อนหลังเสียเวลามากกว่า · " +
      DISCLAIMER + " · " + CTA,
    synonyms: ["digital certified translation", "คำแปล pdf ใช้ได้ไหม", "e-signature translation"],
    cluster: "process",
  },
];

export const MLT_QA_WAVE3: MltQA[] = [...OUTBOUND, ...INBOUND, ...TROUBLE];
export const MLT_DESTINATIONS = DESTS;
